import React, { useState } from 'react';
import { BookOpenIcon, LinkIcon, FileSpreadsheetIcon, ArrowRightIcon, ShieldCheckIcon, Edit3Icon, UploadCloudIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { ImportMethod } from './types';
import { mockImportedStubs } from './mockData';
import { IsbnLookupForm } from './IsbnLookupForm';
import { AsinUrlForm } from './AsinUrlForm';
import { CsvUploadForm } from './CsvUploadForm';
import { ImportReviewQueue } from './ImportReviewQueue';
export default function BookImportPage() {
  const dk = useDarkClasses();
  const [activeMethod, setActiveMethod] = useState<ImportMethod>('isbn');
  const [stubs, setStubs] = useState(mockImportedStubs);
  const handleImportComplete = () => {
    // In a real app, this would fetch the newly imported stubs and append them.
    // For now, we just simulate a refresh or show a success toast.
    console.log('Import complete');
  };
  return <div className={`min-h-screen pt-20 pb-12 ${dk.page}`} data-id="element-18411" data-mp="331mx3jy15y2w">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18412" data-mp="4wpptx122rx6">
        {/* Header */}
        <div className="mb-8" data-id="element-18413" data-mp="9uzjg374tkgo">
          <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-18414" data-mp="9u7omulczqrg">
            Import Your Catalog
          </h1>
          <p className={`text-lg ${dk.textSecondary}`} data-id="element-18415" data-mp="3s2gks03ileq8">
            Lower the friction of bringing your backlist to AfterTide. Import
            100+ titles in minutes.
          </p>
        </div>

        {/* Lifecycle Explanation Band */}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 p-4 mb-10 rounded-xl border ${dk.card} ${dk.borderSubtle}`} data-id="element-18416" data-mp="3n1szlv1yvs3x">
          <div className="flex items-center gap-2" data-id="element-18417" data-mp="3dbg3r6opccih">
            <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 flex items-center justify-center" data-id="element-18418" data-mp="3dc9eeiqs7m5w">
              <UploadCloudIcon className="w-4 h-4" data-id="element-18419" data-mp="34ai9hg42fjd3" />
            </div>
            <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-18420" data-mp="34cunji6pd9kv">
              1. Stub
            </span>
          </div>
          <ArrowRightIcon className={`w-4 h-4 hidden sm:block ${dk.textMuted}`} data-id="element-18421" data-mp="15435odzyj7pz" />
          <div className="flex items-center gap-2" data-id="element-18422" data-mp="111zr3ghzrn0h">
            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 flex items-center justify-center" data-id="element-18423" data-mp="112rfa699eigq">
              <Edit3Icon className="w-4 h-4" data-id="element-18424" data-mp="1stxhcs8jiq8n" />
            </div>
            <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-18425" data-mp="1srb8mp9wy4ou">
              2. Enriched
            </span>
          </div>
          <ArrowRightIcon className={`w-4 h-4 hidden sm:block ${dk.textMuted}`} data-id="element-18426" data-mp="1jsh6ykhq6o4f" />
          <div className="flex items-center gap-2" data-id="element-18427" data-mp="1eu5v647ve3jk">
            <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400 flex items-center justify-center" data-id="element-18428" data-mp="1etedzyf0s8ct">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-18429" data-mp="3e7t4kb40hru" />
            </div>
            <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-18430" data-mp="3pfyetcc4paq6">
              3. Verified
            </span>
          </div>
        </div>

        {/* Import Methods */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-18431" data-mp="3ufb381inepuv">
          <button onClick={() => setActiveMethod('isbn')} className={`p-6 rounded-xl border-2 text-left transition-all ${activeMethod === 'isbn' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/10' : `${dk.border} ${dk.card} hover:border-purple-300 dark:hover:border-purple-700`}`} data-id="element-18432" data-mp="37ygz82wrm83">
            <BookOpenIcon className={`w-8 h-8 mb-4 ${activeMethod === 'isbn' ? 'text-purple-600' : dk.textMuted}`} data-id="element-18433" data-mp="gyac7fusi13g" />
            <h3 className={`font-bold mb-1 ${activeMethod === 'isbn' ? 'text-purple-700 dark:text-purple-400' : dk.textHeading}`} data-id="element-18434" data-mp="qy19fpaqiu78">
              ISBN Lookup
            </h3>
            <p className={`text-sm ${activeMethod === 'isbn' ? 'text-purple-600/80 dark:text-purple-400/80' : dk.textSecondary}`} data-id="element-18435" data-mp="2sp5w06xt94kp">
              Paste ISBNs to auto-fetch metadata from Open Library.
            </p>
          </button>

          <button onClick={() => setActiveMethod('asin')} className={`p-6 rounded-xl border-2 text-left transition-all ${activeMethod === 'asin' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/10' : `${dk.border} ${dk.card} hover:border-purple-300 dark:hover:border-purple-700`}`} data-id="element-18436" data-mp="i4etcc0jbxxe">
            <LinkIcon className={`w-8 h-8 mb-4 ${activeMethod === 'asin' ? 'text-purple-600' : dk.textMuted}`} data-id="element-18437" data-mp="3vh74daifzid6" />
            <h3 className={`font-bold mb-1 ${activeMethod === 'asin' ? 'text-purple-700 dark:text-purple-400' : dk.textHeading}`} data-id="element-18438" data-mp="d496d957mzln">
              ASIN / Amazon URL
            </h3>
            <p className={`text-sm ${activeMethod === 'asin' ? 'text-purple-600/80 dark:text-purple-400/80' : dk.textSecondary}`} data-id="element-18439" data-mp="3gqzrrbf48tke">
              Paste an Amazon link or ASIN to import directly.
            </p>
          </button>

          <button onClick={() => setActiveMethod('csv')} className={`p-6 rounded-xl border-2 text-left transition-all ${activeMethod === 'csv' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/10' : `${dk.border} ${dk.card} hover:border-purple-300 dark:hover:border-purple-700`}`} data-id="element-18440" data-mp="1w1ncpbfii7wj">
            <FileSpreadsheetIcon className={`w-8 h-8 mb-4 ${activeMethod === 'csv' ? 'text-purple-600' : dk.textMuted}`} data-id="element-18441" data-mp="2aywiha5kbe7s" />
            <h3 className={`font-bold mb-1 ${activeMethod === 'csv' ? 'text-purple-700 dark:text-purple-400' : dk.textHeading}`} data-id="element-18442" data-mp="2eyngnyxf4wmc">
              CSV Upload
            </h3>
            <p className={`text-sm ${activeMethod === 'csv' ? 'text-purple-600/80 dark:text-purple-400/80' : dk.textSecondary}`} data-id="element-18443" data-mp="262654ul56wq2">
              Upload a spreadsheet of your entire catalog at once.
            </p>
          </button>
        </div>

        {/* Active Form Area */}
        <div className={`p-6 sm:p-8 rounded-xl border ${dk.card} ${dk.border}`} data-id="element-18444" data-mp="akwgjb4h6n56">
          {activeMethod === 'isbn' && <IsbnLookupForm onImportComplete={handleImportComplete} data-id="element-18445" data-mp="1q8avjuzn3fu" />}
          {activeMethod === 'asin' && <AsinUrlForm onImportComplete={handleImportComplete} data-id="element-18446" data-mp="3sxkoxq0kr0rj" />}
          {activeMethod === 'csv' && <CsvUploadForm onImportComplete={handleImportComplete} data-id="element-18447" data-mp="3nyhokivjhipw" />}
        </div>

        {/* Review Queue */}
        <ImportReviewQueue stubs={stubs} data-id="element-18448" data-mp="3j0ptxh3ifii0" />
      </div>
    </div>;
}